import { FormInstance, Typography, UploadFile, UploadProps } from 'antd';
import { FileOutlined, Loading3QuartersOutlined } from '@ant-design/icons';
import { ErrorMessage } from './ErrorMessage/ErrorMessage';
import { SvgIcon } from 'lib/components/SvgIcon/SvgIcon';
import { kccThemeConstants } from 'config/theme/kccTheme/kccTheme';
import { MAX_FILE_SIZE, SUPPORTED_UPLOAD_IMAGE_ISSUE_TYPES } from 'app/modules/service/utils/constants';
import { formatFileSize } from 'app/modules/service/utils/file';

interface UseUploadPropsParams {
  fileList: UploadFile[];
  setFileList: (fileList: UploadFile[]) => void;
  setIsMaxCount: (isMaxCount: boolean) => void;
  form: FormInstance;
}

interface UseUploadProps {
  uploadPropsWithList: UploadProps;
  uploadPropsNoList: UploadProps;
}

export const useUploadProps = ({
  fileList,
  setFileList,
  setIsMaxCount,
  form,
}: UseUploadPropsParams): UseUploadProps => {
  const baseUploadProps: UploadProps = {
    name: 'file',
    multiple: true,
    action: 'https://httpbin.org/post', // TODO: remove when having actual upload endpoint
    listType: 'picture',
    maxCount: 3,
    fileList,
    onChange(info) {
      const { status } = info.file;

      const updatedFileList = (Array.isArray(info.fileList) ? info.fileList : []).map(file => {
        const isValidType = file.type && SUPPORTED_UPLOAD_IMAGE_ISSUE_TYPES.includes(file.type);
        const isValidSize = file.size && file.size <= MAX_FILE_SIZE;

        if (file.uid === info.file.uid && status === 'done') {
          if (!isValidType || !isValidSize) {
            return { ...file, status: 'error' as const };
          }
        }

        if (file.status === 'done') {
          if (!isValidType || !isValidSize) {
            return { ...file, status: 'error' as const };
          }
        }

        return file;
      });

      setFileList(updatedFileList);
      form.setFieldValue('issuePhotos', updatedFileList);

      const errorMessages: JSX.Element[] = [];

      updatedFileList.forEach(file => {
        const isValidType = file.type && SUPPORTED_UPLOAD_IMAGE_ISSUE_TYPES.includes(file.type);
        const isValidSize = file.size && file.size <= MAX_FILE_SIZE;

        if (file.status === 'error') {
          if (!isValidType) {
            errorMessages.push(<ErrorMessage file={file} type="unsupportedFileType" key={file.uid} />);
          } else if (!isValidSize) {
            errorMessages.push(<ErrorMessage file={file} type="maxSize" key={file.uid} />);
          } else {
            errorMessages.push(<ErrorMessage file={file} type="uploadFailed" key={file.uid} />);
          }
        }
      });

      form.setFields([
        {
          name: 'issuePhotos',
          errors: errorMessages.length > 0 ? (errorMessages as unknown as string[]) : [],
        },
      ]);

      setIsMaxCount(updatedFileList.length >= 3);
    },
    progress: {
      strokeColor: {
        '0%': 'black',
        '100%': 'black',
      },
      format: percent => percent && `${Number.parseFloat(percent.toFixed(2))}%`,
    },
  };

  const uploadPropsWithList: UploadProps = {
    ...baseUploadProps,
    showUploadList: {
      extra: (file: UploadFile) =>
        file.status === 'done' || file.status === 'error' ? (
          <Typography.Text
            style={{
              fontSize: kccThemeConstants.TYPOGRAPHY.FONT_SIZE.SM,
              color: file.status === 'error' ? kccThemeConstants.COLORS.ERROR : kccThemeConstants.COLORS.TEXT,
            }}
          >
            {formatFileSize(file.size)}
          </Typography.Text>
        ) : null,
      removeIcon: (file: UploadFile) => {
        if (file.status === 'uploading') {
          return <SvgIcon name="xmark" />;
        }
        if (file.status === 'removed') {
          return <></>;
        }
        return <SvgIcon name="delete" />;
      },
    },
    isImageUrl: (file: UploadFile) => {
      if (file.status === 'error') {
        return false;
      }
      return file.type?.startsWith('image/') || false;
    },
    iconRender: (file: UploadFile) => {
      if (file.status === 'uploading') {
        return <Loading3QuartersOutlined spin style={{ fontSize: 12 }} />;
      }
      if (file.status === 'error') {
        return <FileOutlined style={{ fontSize: 12 }} />;
      }
      return <FileOutlined style={{ fontSize: 12 }} />;
    },
  };

  const uploadPropsNoList: UploadProps = {
    ...baseUploadProps,
    showUploadList: false,
  };

  return { uploadPropsWithList, uploadPropsNoList };
};
